<script lang="ts" setup>
import type { MaterialSymbolName } from '@/types/materialSymbolNames';

export type IconColour =
  | 'default'
  | 'white'
  | 'disabled'
  | 'primary'
  | 'alert'
  | 'critical'
  | 'warning'
  | 'info'
  | 'purple'
  | 'inherit';

withDefaults(
  defineProps<{
    name: MaterialSymbolName;
    alt?: string;
    colour?: IconColour;
    size?: 'small' | 'medium' | 'large' | 'larger' | 'huge' | 'inherit';
    ghost?: boolean;
  }>(),
  {
    alt: undefined,
    colour: undefined,
    size: 'medium',
    ghost: true,
  },
);

const isFontLoaded = inject<Ref<boolean>>('isFontLoaded', ref(true));
</script>

<template>
  <span :class="{ 'inline-block rounded-md border bg-muted p-2x': !ghost }">
    <i
      v-show="isFontLoaded"
      translate="no"
      :class="['material-symbols-outlined', 'base-icon', size, `colour-${colour}`]"
      aria-hidden="true"
    >
      {{ name }}
    </i>
    <span v-if="alt" class="sr-only">{{ alt }}</span>
  </span>
</template>

<style scoped>
.base-icon {
  line-height: inherit;
  vertical-align: bottom;
  user-select: none;
  color: inherit;
  font-variation-settings: 'FILL' 0;

  /* These classes are nested under .base-icon for the additional specificity */
  &.colour-default {
    color: var(--accent-color-neutral);
  }

  &.colour-white {
    color: var(--color-white);
  }

  &.colour-disabled {
    color: var(--accent-color-neutral-disabled);
  }

  &.colour-primary {
    color: var(--accent-color-primary);
  }

  &.colour-alert {
    color: var(--accent-color-error);
  }

  &.colour-critical {
    color: var(--accent-color-on-fill);
  }

  &.colour-warning {
    color: var(--accent-color-warning);
  }

  &.colour-info {
    color: var(--accent-color-info);
  }

  &.colour-purple {
    color: var(--accent-color-community);
  }

  &.small {
    font-size: 13px;
  }

  &.medium {
    font-size: 18px;
  }

  &.large {
    font-size: 20px;
  }

  &.larger {
    font-size: 35px;
  }

  &.huge {
    font-size: 60px;
  }

  &.inherit {
    font-size: inherit;
  }
}
</style>
